/**
 * Contents of this file were produced with a help of a code generation tool and
 * subsequently reviewed and edited by a human.
 *
 * While some of the code was created by an AI assisted service, manual adjustments
 * have been made to ensure correctness, readability, functionality and compliance
 * to Avalara's coding standards and guidelines. Any future modifications should
 * be approached with caution to avoid overwriting the manual changes.
 *
 * This source code is owned by Avalara Inc. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Proprietary and confidential.
 *
 * For more information, refer to the documentation or contact the development team.
 */

import { forceRefreshSessionAsync } from "../app/sessionSlice";
import store from "../app/store";
import getConfig from "../config";
import { clearLocalStorage, isOneWebDomainRuntime, retainCriticalData } from "./Utils";

const { hostnames } = getConfig();
const { ecmApiHost } = hostnames;

export const getPageTitle = pageTitle => (dispatch, getState) => {
    const { session } = getState();
    return `AvaTax | ${session.activeCompany.name} | ${pageTitle}`;
};

export const getSessionAccessToken = () => {
    return new Promise((resolve, reject) => {
        const MAX_RETRY_ATTEMPTS = 6;
        const INITIAL_DELAY_MS = 100;
        const MAX_DELAY_MS = 2000;
        let attemptCount = 0;
        let isSettled = false;

        const checkTokenAvailability = () => {
            if (isSettled) {
                return;
            }

            attemptCount++;
            const { session } = store.getState();

            // Fast-fail on explicit authentication failure
            if (session?.isAuthenticated === false && session?.isLoading === false) {
                isSettled = true;
                reject(new Error("Authentication failed - user not authenticated"));
                return;
            }

            const isSessionReady =
                session?.accessToken &&
                session?.activeCompany?.id &&
                session?.isLoading === false &&
                session?.isAuthenticated === true;

            if (isSessionReady) {
                isSettled = true;
                resolve({ accessToken: session.accessToken });
                return;
            }

            if (attemptCount >= MAX_RETRY_ATTEMPTS && !isOneWebDomainRuntime()) {
                isSettled = true;
                // Try one final session refresh before giving up
                store
                    .dispatch(forceRefreshSessionAsync())
                    .then(() => {
                        const { session: refreshedSession } = store.getState();
                        if (refreshedSession?.accessToken && refreshedSession?.isAuthenticated) {
                            resolve({ accessToken: refreshedSession.accessToken });
                        } else {
                            const errorMessage = `Session state not ready after ${MAX_RETRY_ATTEMPTS} attempts. Current state: ${JSON.stringify(
                                {
                                    hasAccessToken: !!refreshedSession?.accessToken,
                                    hasCompanyId: !!refreshedSession?.activeCompany?.id,
                                    isLoading: refreshedSession?.isLoading,
                                    isAuthenticated: refreshedSession?.isAuthenticated,
                                }
                            )}`;
                            reject(new Error(errorMessage));
                        }
                    })
                    .catch(() => {
                        const errorMessage = `Session state not ready after ${MAX_RETRY_ATTEMPTS} attempts. Current state: ${JSON.stringify(
                            {
                                hasAccessToken: !!session?.accessToken,
                                hasCompanyId: !!session?.activeCompany?.id,
                                isLoading: session?.isLoading,
                                isAuthenticated: session?.isAuthenticated,
                            }
                        )}`;
                        reject(new Error(errorMessage));
                    });
                return;
            }

            const delay = Math.min(INITIAL_DELAY_MS * Math.pow(2, attemptCount - 1), MAX_DELAY_MS);

            setTimeout(checkTokenAvailability, delay);
        };

        checkTokenAvailability();
    });
};

export const redirectToLoginPage = () => {
    const encodedRedirectUrl = encodeURIComponent(window.location.href);
    retainCriticalData();
    clearLocalStorage();
    window.location = `//${ecmApiHost}/api/v3/auth/login?redirectUrl=${encodedRedirectUrl}`;
};
